import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Clock, Eye, RefreshCw, Search, ShieldCheck, SlidersHorizontal, XCircle } from 'lucide-react';
import {
  approveOutboundMessage,
  cancelOutboundMessage,
  getCommunicationsOutbox,
  getOutboundMessage,
  getOutboundSettings,
  retryOutboundMessage,
  updateOutboundSettings,
} from '../../../data/api';

const MODES = [
  { value: 'hold', label: 'Hold Outbound Messages' },
  { value: 'test', label: 'Test Mode' },
  { value: 'live', label: 'Live Send Enabled' },
];

const STATUS_META = {
  pending: { label: 'Pending', icon: Clock },
  sending: { label: 'Sending...', icon: RefreshCw },
  sent: { label: 'Sent', icon: CheckCircle2 },
  failed: { label: 'Failed', icon: XCircle },
  partially_failed: { label: 'Partially failed', icon: AlertCircle },
  cancelled: { label: 'Cancelled', icon: XCircle },
  stuck: { label: 'Needs Recovery', icon: AlertCircle },
  cancel_requested: { label: 'Stopping...', icon: RefreshCw },
};

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'sending', label: 'Sending' },
  { value: 'stuck', label: 'Needs Recovery' },
  { value: 'partially_failed', label: 'Some Failed' },
  { value: 'failed', label: 'Failed' },
  { value: 'sent', label: 'Sent' },
  { value: 'cancelled', label: 'Cancelled' },
];

const DEFAULT_FILTERS = {
  search: '',
  status: 'all',
  channel: 'all',
  mode: 'all',
  template_type: 'all',
  date_from: '',
  date_to: '',
};

function splitCsv(value) {
  return value.split(',').map(item => item.trim()).filter(Boolean);
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

const STATUS_HELP = {
  pending: 'Waiting to be sent.',
  sending: 'Currently being delivered.',
  sent: 'Successfully delivered to all recipients.',
  failed: 'Delivery failed for every recipient. Open the row to see the failure reason and retry.',
  partially_failed: 'Sent to some recipients, but a few failed. Open the row to see which failed and retry only those.',
  stuck: 'Delivery stalled mid-send. Click "Retry Remaining" to resume sending to the recipients who were not reached.',
  cancelled: 'This send was cancelled before delivery.',
  cancel_requested: 'Cancellation in progress; final state will follow shortly.',
};

function OutboxStatus({ status }) {
  const meta = STATUS_META[status] || { label: status || 'Unknown', icon: AlertCircle };
  const Icon = meta.icon;
  const helpText = STATUS_HELP[status] || '';
  return (
    <span
      className={`communications-status communications-status--${status || 'unknown'}`}
      title={helpText || meta.label}
      aria-label={helpText ? `${meta.label}. ${helpText}` : meta.label}
    >
      <Icon size={14} aria-hidden="true" />
      {meta.label}
    </span>
  );
}

function apiFilters(filters, cursor = null) {
  const params = { limit: 25 };
  Object.entries(filters).forEach(([key, value]) => {
    const normalized = String(value || '').trim();
    if (normalized && normalized !== 'all') {
      params[key] = normalized;
    }
  });
  if (cursor) params.cursor = cursor;
  return params;
}

function purposeForTemplateType(templateType) {
  if (templateType === 'invitation' || templateType === 'formal_invitation' || templateType === 'save_the_date') return 'invitation';
  if (templateType === 'reminder') return 'reminder';
  if (templateType === 'thank_you') return 'thank_you';
  return 'custom';
}

function recommendedActionForStatus(status) {
  if (status === 'pending') return 'Review the recipients and approve only when the message is ready to leave Hold mode.';
  if (status === 'sending') return 'Delivery is in progress. Stop Remaining only affects recipients that have not been sent yet.';
  if (status === 'stuck') return 'This message needs recovery. Retry Remaining will resume only unsent recipients.';
  if (status === 'partially_failed') return 'Some recipients failed. Review the failure reason before retrying failed recipients.';
  if (status === 'failed') return 'No recipients completed successfully. Review the error before retrying.';
  if (status === 'cancelled') return 'This message is closed. Audit history remains available.';
  if (status === 'sent') return 'Delivery processing completed. Use details for audit and recipient review.';
  return 'Select an outbound record to review its operational history.';
}

function deliveryMatchingNote(note) {
  if (!note) return 'Delivery records will appear here when available.';
  return note
    .replace(/Mock preview only\.\s*/i, 'Local preview only. ')
    .replace(/provider reconciliation/ig, 'delivery matching');
}

export default function OutboxPanel({ onUseAgain = null }) {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [health, setHealth] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [cursorStack, setCursorStack] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [expandedRecipients, setExpandedRecipients] = useState({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Advanced Delivery Controls (queue approval / cancel / stop remaining) are
  // hidden by default. The backend HOLD/queue architecture remains; a future
  // Advanced Delivery Controls panel flips this flag to re-expose the UI.
  const showAdvancedControls = false;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [message, setMessage] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const pendingCount = health?.pending ?? items.filter(item => item.status === 'pending').length;

  const load = async (cursorValue = cursor, nextFilters = filters) => {
    setLoading(true);
    setMessage(null);
    try {
      const [settingsData, outboxData] = await Promise.all([
        getOutboundSettings(),
        getCommunicationsOutbox(apiFilters(nextFilters, cursorValue)),
      ]);
      setSettings(settingsData);
      setDraft({
        safety_enabled: Boolean(settingsData.safety_enabled),
        send_mode: settingsData.send_mode || 'live',
        test_email_recipients: (settingsData.test_email_recipients || []).join(', '),
        test_sms_recipients: (settingsData.test_sms_recipients || []).join(', '),
        sms_provider_enabled: Boolean(settingsData.sms_provider_enabled),
      });
      setItems(outboxData.items || []);
      setHealth(outboxData.health || null);
      setNextCursor(outboxData.next_cursor || null);
      setHasMore(Boolean(outboxData.has_more));
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Unable to load outbox safety data.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(null, DEFAULT_FILTERS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = async () => {
    setCursor(null);
    setCursorStack([]);
    await load(null, filters);
  };

  const resetFilters = async () => {
    setFilters(DEFAULT_FILTERS);
    setCursor(null);
    setCursorStack([]);
    await load(null, DEFAULT_FILTERS);
  };

  const goNextPage = async () => {
    if (!nextCursor) return;
    setCursorStack(prev => [...prev, cursor]);
    setCursor(nextCursor);
    await load(nextCursor, filters);
  };

  const goPreviousPage = async () => {
    if (!cursorStack.length) return;
    const previousStack = cursorStack.slice(0, -1);
    const previousCursor = cursorStack[cursorStack.length - 1] || null;
    setCursorStack(previousStack);
    setCursor(previousCursor);
    await load(previousCursor, filters);
  };

  const saveSettings = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const updated = await updateOutboundSettings({
        safety_enabled: draft.safety_enabled,
        send_mode: draft.send_mode,
        test_email_recipients: splitCsv(draft.test_email_recipients),
        test_sms_recipients: splitCsv(draft.test_sms_recipients),
        sms_provider_enabled: draft.sms_provider_enabled,
      });
      setSettings(updated);
      setMessage({ type: 'success', text: 'Settings saved.' });
      setSettingsOpen(false);
      await load();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Unable to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  const review = async (item) => {
    if (item.source !== 'outbound_messages') {
      setSelected({ message: item, recipients: [] });
      return;
    }
    setMessage(null);
    try {
      const detail = await getOutboundMessage(item.id);
      setSelected(detail);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Unable to load outbound message.' });
    }
  };

  const approve = async (id) => {
    setActionId(id);
    setMessage(null);
    try {
      const result = await approveOutboundMessage(id);
      setMessage({ type: result.status === 'failed' ? 'error' : 'success', text: `Approval finished: ${result.status}. Sent ${result.sent_count || 0}, failed ${result.failed_count || 0}.` });
      await load();
      if (selected?.message?._id === id) {
        await review({ id, source: 'outbound_messages' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Unable to approve outbound message.' });
    } finally {
      setActionId(null);
    }
  };

  const retry = async (id) => {
    setActionId(id);
    setMessage(null);
    try {
      const result = await retryOutboundMessage(id);
      setMessage({ type: result.status === 'sent' ? 'success' : 'error', text: `Retry finished: ${result.status}. Sent ${result.sent_count || 0}, failed ${result.failed_count || 0}.` });
      await load();
      if (selected?.message?._id === id) {
        await review({ id, source: 'outbound_messages' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Unable to retry outbound message.' });
    } finally {
      setActionId(null);
    }
  };

  const cancel = async (id) => {
    setActionId(id);
    setMessage(null);
    try {
      await cancelOutboundMessage(id, 'Cancelled from Communications Outbox');
      setMessage({ type: 'success', text: 'Pending outbound message cancelled. Audit history remains available.' });
      await load();
      if (selected?.message?._id === id) {
        await review({ id, source: 'outbound_messages' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Unable to cancel outbound message.' });
    } finally {
      setActionId(null);
    }
  };

  const stopRemaining = async (id) => {
    setActionId(id);
    setMessage(null);
    try {
      const result = await cancelOutboundMessage(id, 'Stop remaining recipients from Communications Outbox');
      setMessage({ type: 'success', text: `Stop remaining recorded. Sent emails remain sent; unsent recipients were skipped. Current status: ${result.status}.` });
      await load();
      if (selected?.message?._id === id) {
        await review({ id, source: 'outbound_messages' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Unable to stop remaining recipients.' });
    } finally {
      setActionId(null);
    }
  };

  const handleUseAgain = (item) => {
    if (!onUseAgain) return;
    onUseAgain({
      type: 'outbox_use_again',
      purpose: purposeForTemplateType(item.template_type),
      channel: item.channel === 'email' ? 'email' : 'email',
      templateId: item.template_id || '',
      sourceOutboundId: item.id,
      subject: item.subject || '',
    });
  };

  const toggleRecipient = (recipientId) => {
    setExpandedRecipients(prev => ({ ...prev, [recipientId]: !prev[recipientId] }));
  };

  const visibleTimeline = selected?.timeline?.filter(event => !event.collapsed) || [];
  const collapsedTimeline = selected?.timeline?.filter(event => event.collapsed) || [];

  const requestAction = (action, item) => {
    setConfirmAction({ action, item });
  };

  const performConfirmedAction = async () => {
    if (!confirmAction?.item) return;
    const { action, item } = confirmAction;
    setConfirmAction(null);
    if (action === 'approve') {
      await approve(item.id);
    } else if (action === 'retry') {
      await retry(item.id);
    } else if (action === 'stop') {
      await stopRemaining(item.id);
    } else if (action === 'cancel') {
      await cancel(item.id);
    }
  };

  const confirmationContent = useMemo(() => {
    if (!confirmAction?.item) return null;
    const { action, item } = confirmAction;
    const recipientCount = item.recipient_count || item.sendable_count || 0;
    const mode = item.mode_at_creation || item.mode || settings?.send_mode || 'hold';
    const subject = item.subject || 'Outbound communication';

    if (action === 'approve') {
      return {
        title: 'Approve Outbound Message?',
        body: 'Approving can contact real guests. Confirm the audience and content before this message leaves Hold.',
        confirmLabel: 'Approve and Send',
        danger: mode === 'live' || recipientCount > 1,
        details: [
          ['Recipients', `${recipientCount} recipient(s)`],
          ['Subject', subject],
          ['Safety mode', MODES.find(option => option.value === mode)?.label || mode],
        ],
      };
    }
    if (action === 'retry') {
      return {
        title: 'Retry Remaining Recipients?',
        body: 'Only failed, pending, or unsent recipients will be retried. Recipients already accepted by the provider will not be resent.',
        confirmLabel: item.status === 'stuck' ? 'Retry Remaining' : 'Retry Failed',
        danger: false,
        details: [
          ['Recipients', `${recipientCount} original recipient(s)`],
          ['Subject', subject],
          ['Current status', STATUS_META[item.status]?.label || item.status],
        ],
      };
    }
    if (action === 'stop') {
      return {
        title: 'Stop Remaining Recipients?',
        body: 'This cannot recall emails that already left. It only skips recipients that have not been processed yet.',
        confirmLabel: 'Stop Remaining',
        danger: true,
        details: [
          ['Recipients', `${recipientCount} original recipient(s)`],
          ['Subject', subject],
          ['Current status', STATUS_META[item.status]?.label || item.status],
        ],
      };
    }
    return {
      title: 'Cancel Pending Message?',
      body: 'Cancelling keeps the audit record but prevents unsent recipients from receiving this pending message.',
      confirmLabel: 'Cancel Message',
      danger: true,
      details: [
        ['Recipients', `${recipientCount} recipient(s)`],
        ['Subject', subject],
        ['Current status', STATUS_META[item.status]?.label || item.status],
      ],
    };
  }, [confirmAction, settings?.send_mode]);

  return (
    <div className="communications-panel communications-outbox" data-testid="communications-outbox-panel">
      <div className="communications-panel__toolbar">
        <div>
          <h2>Outbox</h2>
          <p>Review held, sent, failed, partially failed, and cancelled outbound communications.</p>
        </div>
        <button type="button" className="communications-button communications-button--ghost" onClick={load} disabled={loading}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {message && (
        <div className={`communications-callout communications-callout--${message.type}`}>
          {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Outbound Safety Mode banner intentionally hidden. The HOLD/TEST/LIVE
          architecture remains in the backend and reachable via the settings
          endpoint; a future Advanced Delivery Controls panel will re-expose
          this UI by setting showAdvancedControls = true on the relevant rows
          and surfacing setSettingsOpen() elsewhere. */}

      <section className="communications-outbox-filters" aria-label="Outbox filters">
        <label className="communications-search communications-outbox-search">
          <Search size={16} />
          <input
            value={filters.search}
            onChange={(event) => updateFilter('search', event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') applyFilters();
            }}
            placeholder="Search subject, recipient, template, ID..."
          />
        </label>
        <div className="communications-outbox-status-chips" aria-label="Status filters">
          {STATUS_FILTERS.map(option => (
            <button
              key={option.value}
              type="button"
              className={filters.status === option.value ? 'communications-chip communications-chip--active' : 'communications-chip'}
              title={STATUS_HELP[option.value] || option.label}
              aria-label={STATUS_HELP[option.value] ? `${option.label}: ${STATUS_HELP[option.value]}` : option.label}
              onClick={() => {
                const nextFilters = { ...filters, status: option.value };
                setFilters(nextFilters);
                setCursor(null);
                setCursorStack([]);
                load(null, nextFilters);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
        <button type="button" className="communications-button communications-button--ghost" onClick={() => setFiltersOpen(open => !open)}>
          <SlidersHorizontal size={16} />
          Filters
        </button>
        <button type="button" className="communications-button communications-button--primary" onClick={applyFilters}>
          Apply
        </button>
        {filtersOpen && (
          <div className="communications-outbox-filter-drawer">
            <label>
              <span>Channel</span>
              <select value={filters.channel} onChange={(event) => updateFilter('channel', event.target.value)}>
                <option value="all">All channels</option>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
            </label>
            <label>
              <span>Mode</span>
              <select value={filters.mode} onChange={(event) => updateFilter('mode', event.target.value)}>
                <option value="all">All modes</option>
                <option value="hold">Hold</option>
                <option value="test">Test</option>
                <option value="live">Live</option>
              </select>
            </label>
            <label>
              <span>Template type</span>
              <select value={filters.template_type} onChange={(event) => updateFilter('template_type', event.target.value)}>
                <option value="all">All templates</option>
                <option value="invitation">Invitation</option>
                <option value="reminder">Reminder</option>
                <option value="thank_you">Thank You</option>
                <option value="custom">Custom</option>
              </select>
            </label>
            <label>
              <span>From</span>
              <input type="date" value={filters.date_from} onChange={(event) => updateFilter('date_from', event.target.value)} />
            </label>
            <label>
              <span>To</span>
              <input type="date" value={filters.date_to} onChange={(event) => updateFilter('date_to', event.target.value)} />
            </label>
            <button type="button" className="communications-button communications-button--ghost" onClick={resetFilters}>
              Reset
            </button>
          </div>
        )}
      </section>

      <div className="communications-kpi-row communications-outbox-kpis">
        <div className="communications-kpi">
          <span>Pending Review</span>
          <strong>{pendingCount}</strong>
        </div>
        <div className="communications-kpi">
          <span>Needs Attention</span>
          <strong>{health?.needs_attention ?? 0}</strong>
        </div>
        <div className="communications-kpi">
          <span>Needs Recovery</span>
          <strong>{health?.stuck ?? 0}</strong>
        </div>
        <div className="communications-kpi">
          <span>Current Mode</span>
          <strong>{settings?.safety_enabled ? settings.send_mode : 'legacy'}</strong>
        </div>
        <div className="communications-kpi">
          <span>SMS Provider</span>
          <strong>{settings?.sms_provider_enabled ? 'enabled' : 'disabled'}</strong>
        </div>
      </div>

      {health?.warnings && (health.warnings.stuck_messages || health.warnings.failed_threshold || health.warnings.queue_depth) && (
        <div className="communications-callout communications-callout--error">
          <AlertCircle size={16} />
          <span>
            {health.warnings.stuck_messages ? 'One or more outbound messages need recovery. ' : ''}
            {health.warnings.failed_threshold ? 'Failed sends are above the review threshold. ' : ''}
            {health.warnings.queue_depth ? 'Pending queue depth is high. ' : ''}
          </span>
        </div>
      )}

      <div className="communications-outbox-layout">
        <div className="communications-outbox-list">
          <div className="communications-table__head communications-outbox-row">
            <span>Message</span>
            <span>Channel</span>
            <span>Status</span>
            <span>Created</span>
            <span>Actions</span>
          </div>
          {loading ? (
            <div className="communications-empty"><Clock size={24} /><p>Loading outbox...</p></div>
          ) : items.length ? items.map(item => (
            <div key={`${item.source}-${item.id}`} className="communications-table__row communications-outbox-row">
              <span className="communications-outbox-message">
                <strong>{item.subject || 'Outbound communication'}</strong>
                <small>{item.preview || `${item.recipient_count || 0} recipient(s)`}</small>
              </span>
              <span>{item.channel}</span>
              <OutboxStatus status={item.status} />
              <span>{formatDate(item.created_at || item.sent_at)}</span>
              <span className="communications-outbox-actions">
                <button type="button" onClick={() => review(item)} title="Review details" aria-label="Review details"><Eye size={15} /></button>
                {/* Approval-queue controls (Approve / Cancel / Stop Remaining) hidden by default.
                    Backend HOLD/queue architecture remains intact; future Advanced Delivery
                    Controls flip showAdvancedControls=true to re-expose. */}
                {showAdvancedControls && item.source === 'outbound_messages' && item.status === 'pending' && (
                  <>
                    <button type="button" onClick={() => requestAction('approve', item)} disabled={actionId === item.id}>Approve</button>
                    <button type="button" onClick={() => requestAction('cancel', item)} disabled={actionId === item.id}>Cancel</button>
                  </>
                )}
                {showAdvancedControls && item.source === 'outbound_messages' && item.status === 'sending' && (
                  <button type="button" onClick={() => requestAction('stop', item)} disabled={actionId === item.id}>Stop Remaining</button>
                )}
                {/* Retry on failure stays visible — failed sends are normal SaaS UX. */}
                {item.source === 'outbound_messages' && item.status === 'stuck' && (
                  <button type="button" onClick={() => requestAction('retry', item)} disabled={actionId === item.id}>Retry Remaining</button>
                )}
                {item.source === 'outbound_messages' && ['failed', 'partially_failed'].includes(item.status) && (
                  <button type="button" onClick={() => requestAction('retry', item)} disabled={actionId === item.id}>Retry Failed</button>
                )}
                {showAdvancedControls && item.source === 'outbound_messages' && item.status === 'cancel_requested' && (
                  <button type="button" disabled>Stopping...</button>
                )}
                {item.source === 'outbound_messages' && !['pending', 'sending', 'cancel_requested'].includes(item.status) && (
                  <button type="button" onClick={() => handleUseAgain(item)} disabled={!onUseAgain}>Use Again</button>
                )}
              </span>
            </div>
          )) : (
            <div className="communications-empty communications-empty--large">
              <Clock size={26} />
              <h3>No outbound records yet</h3>
              <p>Sent messages will appear here.</p>
            </div>
          )}
          <div className="communications-outbox-pagination">
            <button type="button" className="communications-button communications-button--ghost" onClick={goPreviousPage} disabled={!cursorStack.length || loading}>
              Previous
            </button>
            <span>{cursor ? 'Next page' : 'First page'}</span>
            <button type="button" className="communications-button communications-button--ghost" onClick={goNextPage} disabled={!hasMore || loading}>
              Next
            </button>
          </div>
        </div>

        <aside className="communications-outbox-detail">
          {selected ? (
            <>
	              <p className="communications-panel__eyebrow">Operational Record</p>
	              <h3>{selected.message?.subject || 'Outbound Details'}</h3>
	              <div className="communications-outbox-guidance">
	                <OutboxStatus status={selected.message?.status} />
	                <p>{recommendedActionForStatus(selected.message?.status)}</p>
	              </div>
	              <div className="communications-send-summary">
                <div className="communications-send-summary__row"><span>Status</span><strong>{selected.message?.status}</strong></div>
                <div className="communications-send-summary__row"><span>Channel</span><strong>{selected.message?.channel}</strong></div>
                <div className="communications-send-summary__row"><span>Mode</span><strong>{selected.message?.mode_at_creation || selected.message?.mode || '-'}</strong></div>
                <div className="communications-send-summary__row"><span>Failure</span><strong>{selected.message?.failure_reason || '-'}</strong></div>
                <div className="communications-send-summary__row"><span>Attempts</span><strong>{selected.message?.attempt_count || 0}</strong></div>
                <div className="communications-send-summary__row"><span>Last Error</span><strong>{selected.message?.last_error || '-'}</strong></div>
                <div className="communications-send-summary__row"><span>Recovery Window</span><strong>{formatDate(selected.message?.lock_expires_at)}</strong></div>
              </div>
              {visibleTimeline.length > 0 && (
                <section className="communications-outbox-timeline">
                  <h4>Audit Timeline</h4>
                  {visibleTimeline.map(event => (
                    <div key={event.id || `${event.type}-${event.created_at}`} className="communications-outbox-timeline__event">
                      <span />
                      <div>
                        <strong>{event.label}</strong>
                        <small>{formatDate(event.created_at)}{event.status ? ` - ${event.status}` : ''}</small>
                      </div>
                    </div>
                  ))}
                  {collapsedTimeline.length > 0 && (
                    <details>
                      <summary>{collapsedTimeline.length} background detail(s)</summary>
                      {collapsedTimeline.map(event => (
                        <div key={event.id || `${event.type}-${event.created_at}`} className="communications-outbox-timeline__event communications-outbox-timeline__event--muted">
                          <span />
                          <div>
                            <strong>{event.label}</strong>
                            <small>{formatDate(event.created_at)}{event.status ? ` - ${event.status}` : ''}</small>
                          </div>
                        </div>
                      ))}
                    </details>
                  )}
                </section>
              )}
              {selected.provider_reconciliation && (
                <section className="communications-outbox-reconciliation">
                  <h4>Delivery Matching</h4>
                  <p>{deliveryMatchingNote(selected.provider_reconciliation.note)}</p>
                  <span>Matched delivery records: {selected.provider_reconciliation.linked_email_log_count}</span>
                  <span>Needs review: {selected.provider_reconciliation.unmatched_email_log_count}</span>
                </section>
              )}
              {selected.recipients?.length > 0 && (
                <div className="communications-outbox-recipient-list">
                  {selected.recipients.map(recipient => (
                    <div key={recipient._id}>
                      <button type="button" onClick={() => toggleRecipient(recipient._id)} className="communications-outbox-recipient-list__summary">
                        <strong>{recipient.guest_name || recipient.email || recipient.phone}</strong>
                        <span>{recipient.status}{recipient.failure_reason ? ` - ${recipient.failure_reason}` : ''}</span>
                        <small>{recipient.attempt_count || 0} attempt(s){recipient.last_error ? ` - ${recipient.last_error}` : ''}</small>
                      </button>
                      {expandedRecipients[recipient._id] && (
                        <dl>
                          <div><dt>Email</dt><dd>{recipient.email || '-'}</dd></div>
                          <div><dt>Provider ID</dt><dd>{recipient.provider_message_id || '-'}</dd></div>
                          <div><dt>Email Log ID</dt><dd>{recipient.email_log_id || '-'}</dd></div>
                          <div><dt>Last Attempt</dt><dd>{formatDate(recipient.last_attempt_at)}</dd></div>
                          <div><dt>Last Status</dt><dd>{formatDate(recipient.last_status_change_at)}</dd></div>
                          <div><dt>Snapshot</dt><dd>{recipient.message_snapshot?.subject || 'No subject snapshot'}</dd></div>
                        </dl>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="communications-empty">
              <Eye size={22} />
              <p>Select an outbound record to review its status and recipients.</p>
            </div>
          )}
        </aside>
      </div>

      {settingsOpen && draft && (
        <div className="communications-confirm-backdrop" role="dialog" aria-modal="true" aria-label="Send settings">
          <div className="communications-confirm communications-outbox-settings-modal">
            <button type="button" className="communications-confirm__close" onClick={() => setSettingsOpen(false)} aria-label="Close safety settings">
              <XCircle size={16} />
            </button>
            <div className="communications-preview-modal__hero">
              <div>
                <p className="communications-panel__eyebrow">Protected Controls</p>
                <h3>Outbound Safety</h3>
                <span>Adjust only when you intend to change how admin-initiated messages leave the Hub.</span>
              </div>
              <ShieldCheck size={24} />
            </div>
            <div className="communications-outbox-settings">
              <label>
                <span>Safety layer</span>
                <select value={draft.safety_enabled ? 'enabled' : 'disabled'} onChange={(e) => setDraft(prev => ({ ...prev, safety_enabled: e.target.value === 'enabled' }))}>
                  <option value="disabled">Disabled - legacy behavior</option>
                  <option value="enabled">Enabled - enforce selected mode</option>
                </select>
              </label>
              <label>
                <span>Mode</span>
                <select value={draft.send_mode} onChange={(e) => setDraft(prev => ({ ...prev, send_mode: e.target.value }))}>
                  <option value="hold">Hold Outbound Messages</option>
                  <option value="test">Test Mode</option>
                  <option value="live">Live Send Enabled</option>
                </select>
              </label>
              <label>
                <span>Test email recipients</span>
                <input value={draft.test_email_recipients} onChange={(e) => setDraft(prev => ({ ...prev, test_email_recipients: e.target.value }))} placeholder="test@example.com" />
              </label>
              <label>
                <span>Test SMS recipients</span>
                <input value={draft.test_sms_recipients} onChange={(e) => setDraft(prev => ({ ...prev, test_sms_recipients: e.target.value }))} placeholder="+15551234567" />
              </label>
              <label>
                <span>SMS provider</span>
                <select value={draft.sms_provider_enabled ? 'enabled' : 'disabled'} onChange={(e) => setDraft(prev => ({ ...prev, sms_provider_enabled: e.target.value === 'enabled' }))}>
                  <option value="disabled">Disabled - refuse SMS live sends</option>
                  <option value="enabled">Enabled - provider configured</option>
                </select>
              </label>
            </div>
            <div className="communications-confirm__actions">
              <button type="button" className="communications-button communications-button--ghost" onClick={() => setSettingsOpen(false)}>
                Close
              </button>
              <button type="button" className="communications-button communications-button--primary" onClick={saveSettings} disabled={saving}>
                Save Safety Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAction && confirmationContent && (
        <div className="communications-confirm-backdrop" role="dialog" aria-modal="true" aria-label={confirmationContent.title}>
          <div className="communications-confirm communications-live-confirm">
            <button
              type="button"
              className="communications-confirm__close"
              onClick={() => setConfirmAction(null)}
              aria-label="Close confirmation"
            >
              <XCircle size={16} />
            </button>
            <AlertCircle size={24} />
            <h3>{confirmationContent.title}</h3>
            <p>{confirmationContent.body}</p>
            <div className="communications-live-confirm__details">
              {confirmationContent.details.map(([label, value]) => (
                <div key={label} className="communications-send-summary__row">
                  <span>{label}</span>
                  <strong>{value || '-'}</strong>
                </div>
              ))}
            </div>
            <div className="communications-confirm__actions">
              <button type="button" className="communications-button communications-button--ghost" onClick={() => setConfirmAction(null)}>
                Keep Reviewing
              </button>
              <button
                type="button"
                className={`communications-button ${confirmationContent.danger ? 'communications-button--danger' : 'communications-button--primary'}`}
                onClick={performConfirmedAction}
                disabled={actionId === confirmAction.item.id}
              >
                {confirmationContent.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
